(() => {
  if (customElements.get("store-provenance")) return;

  class StoreProvenance extends HTMLElement {
    constructor() {
      super();
      this.cleanup = [];
    }

    connectedCallback() {
      this.items = Array.from(this.querySelectorAll("[data-point]"));
      this.mapPoints = Array.from(this.querySelectorAll("[data-map-point]"));
      this.details = Array.from(this.querySelectorAll("[data-detail]"));
      this.modals = Array.from(
        this.querySelectorAll("[data-provenance-modal]"),
      );
      this.desktopMap = this.querySelector(".store-provenance__map");
      this.closeButtons = Array.from(
        this.querySelectorAll(
          "[data-provenance-close], [data-provenance-overlay]",
        ),
      );
      this.supportsHover = window.matchMedia(
        "(hover: hover) and (pointer: fine)",
      ).matches;
      this.isModalOpen = false;
      this.isMobileView = window.matchMedia("(max-width: 1023px)");

      if (!this.items.length && !this.mapPoints.length) {
        return;
      }

      this.onActivate = this.onActivate.bind(this);
      this.onClick = this.onClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onDeactivate = this.onDeactivate.bind(this);
      this.onContainerFocusOut = this.onContainerFocusOut.bind(this);
      this.onClose = this.onClose.bind(this);
      this.onDocumentKeydown = this.onDocumentKeydown.bind(this);
      this.onModalAnimationEnd = this.onModalAnimationEnd.bind(this);
      this.modalHideTimer = null;
      this.clearTimer = null;

      if (this.supportsHover) {
        this.addListener(this, "pointerover", this.onActivate);
        this.addListener(this, "pointerout", this.onDeactivate);
      }
      if (!this.isMobileView.matches) {
        this.addListener(this, "focusin", this.onActivate);
      }
      if (!this.isMobileView.matches) {
        this.addListener(this, "focusout", this.onContainerFocusOut);
      }
      this.addListener(this, "click", this.onClick);
      this.addListener(this, "keydown", this.onKeydown);
      this.closeButtons.forEach((button) => {
        this.addListener(button, "click", this.onClose);
      });
      this.modals.forEach((modal) => {
        this.addListener(modal, "animationend", this.onModalAnimationEnd);
      });
      this.addListener(document, "keydown", this.onDocumentKeydown);
      this.bindEditorBlocks();

      if (!this.isMobileLayout()) {
        const firstId =
          (this.items[0] && this.items[0].dataset.pointId) ||
          (this.mapPoints[0] && this.mapPoints[0].dataset.pointId) ||
          null;
        if (firstId) {
          this.setActive(firstId);
        }
      }
    }

    disconnectedCallback() {
      this.cleanup.forEach((remove) => remove());
      this.cleanup = [];
    }

    addListener(el, event, handler, options) {
      if (!el) return;
      el.addEventListener(event, handler, options);
      this.cleanup.push(() => el.removeEventListener(event, handler, options));
    }

    bindEditorBlocks() {
      if (!window.webvista || typeof window.webvista.bindShopifyEditorBlockEvents !== "function") {
        return;
      }

      const unbind = window.webvista.bindShopifyEditorBlockEvents({
        root: this,
        getBlockElement: (blockId) =>
          this.querySelector(`[data-point-id="${blockId}"]`),
        onSelect: ({ blockId }) => {
          this.setActive(blockId);
          if (this.isMobileLayout()) {
            this.openModal();
          }
        },
        onDeselect: ({ blockId }) => {
          if (this.activeId !== blockId) {
            return;
          }
          this.setActive(null);
        },
      });

      this.cleanup.push(() => {
        if (typeof unbind === "function") {
          unbind();
        }
      });
    }

    isMobileLayout() {
      if (this.isMobileView.matches) {
        return true;
      }
      if (!this.desktopMap) {
        return false;
      }
      return window.getComputedStyle(this.desktopMap).display === "none";
    }

    onActivate(event) {
      const detail = event.target.closest("[data-detail]");
      if (detail && this.contains(detail)) {
        if (this.clearTimer) {
          window.clearTimeout(this.clearTimer);
          this.clearTimer = null;
        }
        return;
      }
      const target = event.target.closest("[data-point], [data-map-point]");
      if (!target || !this.contains(target)) {
        return;
      }
      const id = target.dataset.pointId;
      if (id) {
        this.setActive(id);
      }
    }

    onClick(event) {
      const target = event.target.closest("[data-point], [data-map-point]");
      if (!target || !this.contains(target)) {
        return;
      }
      const id = target.dataset.pointId;
      if (!id) {
        return;
      }
      this.setActive(id);
      if (this.isMobileLayout()) {
        this.openModal();
      }
    }

    onDeactivate(event) {
      if (this.isModalOpen) {
        return;
      }
      const detail = event.target.closest("[data-detail]");
      const target = detail || event.target.closest("[data-point], [data-map-point]");
      if (!target || !this.contains(target)) {
        return;
      }
      const related = event.relatedTarget;
      if (related && target.contains(related)) {
        return;
      }
      if (related && related.closest && related.closest("[data-detail], [data-point], [data-map-point]")) {
        return;
      }
      if (this.clearTimer) {
        window.clearTimeout(this.clearTimer);
      }
      this.clearTimer = window.setTimeout(() => {
        this.setActive(null);
      }, 400);
    }

    onContainerFocusOut(event) {
      if (this.isModalOpen) {
        return;
      }
      if (!this.contains(event.relatedTarget)) {
        if (this.clearTimer) {
          window.clearTimeout(this.clearTimer);
        }
        this.clearTimer = window.setTimeout(() => {
          this.setActive(null);
        }, 400);
      }
    }

    onKeydown(event) {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        this.onActivate(event);
      }
    }

    onClose(event) {
      if (event) {
        event.preventDefault();
      }
      this.closeAll();
    }

    onDocumentKeydown(event) {
      if (event.key === "Escape") {
        this.closeAll();
      }
    }

    onModalAnimationEnd(event) {
      const modal = event.currentTarget;
      if (!modal || event.target !== modal) {
        return;
      }
      if (modal.classList.contains("is-closing")) {
        modal.classList.remove("is-closing");
        modal.classList.remove("flex");
        modal.classList.add("hidden");
      }
    }

    openModal() {
      if (!this.isMobileLayout()) {
        return;
      }
      const modal = this.modals[0];
      if (!modal || this.isModalOpen) {
        return;
      }
      if (this.modalHideTimer) {
        window.clearTimeout(this.modalHideTimer);
        this.modalHideTimer = null;
      }
      this.closeAll({ resetActive: false, skipHide: true });
      modal.classList.remove("hidden");
      modal.classList.add("flex");
      modal.classList.remove("is-closing");
      modal.classList.add("is-opening");
      modal.setAttribute("aria-hidden", "false");
      document.body.style.overflow = "hidden";
      this.isModalOpen = true;
    }

    closeAll({ resetActive = true, skipHide = false } = {}) {
      if (this.modalHideTimer) {
        window.clearTimeout(this.modalHideTimer);
        this.modalHideTimer = null;
      }
      if (resetActive) {
        this.setActive(null);
      }
      this.modals.forEach((modal) => {
        modal.classList.remove("is-opening");
        modal.classList.add("is-closing");
        modal.setAttribute("aria-hidden", "true");
      });
      if (!skipHide) {
        this.modalHideTimer = window.setTimeout(() => {
          this.modals.forEach((modal) => {
            modal.classList.remove("flex");
            modal.classList.add("hidden");
            modal.classList.remove("is-closing");
          });
        }, 300);
      }
      document.body.style.overflow = "";
      this.isModalOpen = false;
    }

    setActive(id) {
      if (this.activeId === id) {
        return;
      }

      if (this.clearTimer) {
        window.clearTimeout(this.clearTimer);
        this.clearTimer = null;
      }

      this.activeId = id;

      this.items.forEach((item) => {
        const isActive = id && item.dataset.pointId === id;
        item.classList.toggle("is-active", isActive);
        item.setAttribute("aria-pressed", isActive ? "true" : "false");
      });

      this.mapPoints.forEach((point) => {
        const isActive = id && point.dataset.pointId === id;
        point.classList.toggle("is-active", isActive);
        point.setAttribute("aria-pressed", isActive ? "true" : "false");
      });

      this.details.forEach((detail) => {
        const isActive = id && detail.dataset.pointId === id;
        detail.classList.toggle("is-active", isActive);
      });
    }
  }

  customElements.define("store-provenance", StoreProvenance);
})();
